import React, { useRef, useState } from "react";
import { reduxForm } from "redux-form";
import { CircularProgress } from "@mui/material";
import { notification } from "antd";
import { useDispatch } from "react-redux";
import styles from "../../../JobStyle.module.css";
import JobSwmsFormV3SectionRegistry from "./JobSwmsFormV3SectionRegistry.jsx";
import { serializeSubmitPayloadV3 } from "./SwmsFormV3Serializer";
import { saveSwmsForm } from "../../../../../../actions/v2/JobSwmsActions";
import { Strings } from "../../../../../../dataProvider/localize";
import SwmsResendConfirmModal from "../../JobSwmsTask/SwmsResendConfirmModal";
import SwmsCancelApproverConfirmModal from "../../JobSwmsTask/SwmsCancelApproverConfirmModal";
import { buildDocumentProjectDetailsChanges, createSwmsEditContext } from "./editHistory/swmsEditContext";
import { buildJobStepsChanges } from "./editHistory/jobStepsHistory";
import { buildEmergencyContactsChanges } from "./editHistory/emergencyContactsHistory";
import { buildMethodItemSectionsChanges } from "./editHistory/methodItemHistory";
import { buildSwmsContentManagementChanges } from "./editHistory/swmsContentManagementHistory";

function JobSwmsFormV3Shell({ onClose, swmsData, initialValues, baselineValues, internalRevisionId = 0, currentVersion, handleSubmit, reset, loading, change, jobId, swmsId, approvalSubmissionHistory, swmsTitle, swmsNumber, refreshForm, swmsStatus, saveRequest, discardRequest, pristine, onDirtyChange, onSavingChange }) {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [resendReviewer, setResendReviewer] = useState(null);
  const [cancelApprover, setCancelApprover] = useState(null);
  const lastDiscardRequest = useRef(0);

  React.useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(!pristine);
    }
  }, [pristine, onDirtyChange]);

  React.useEffect(() => {
    if (discardRequest && discardRequest !== lastDiscardRequest.current) {
      lastDiscardRequest.current = discardRequest;
      reset();
    }
  }, [discardRequest, reset]);

  React.useEffect(() => {
    if (saveRequest) {
      handleSubmit(onSubmit)();
    }
  }, [saveRequest]);

  const resolveTitle = (values) => {
    if (values && typeof values.title === "string" && values.title.trim() !== "") {
      return values.title.trim();
    }

    const projectDetailsTitle = values
      && values.sections
      && values.sections.document_project_details
      && values.sections.document_project_details.data
      && typeof values.sections.document_project_details.data.swms_name === "string"
      ? values.sections.document_project_details.data.swms_name
      : "";

    if (projectDetailsTitle && projectDetailsTitle.trim() !== "") {
      return projectDetailsTitle.trim();
    }

    return swmsData && typeof swmsData.title === "string" ? swmsData.title : "";
  };

  const onSubmit = (values) => {
    if (submitting) {
      return;
    }

    const submitPayload = serializeSubmitPayloadV3(values, swmsData || {});
    const baselineFormValues = baselineValues || initialValues || {};
    const initialPayload = serializeSubmitPayloadV3(baselineFormValues, swmsData || {});
    const initialDocument = {
      sections: initialPayload && initialPayload.sections ? initialPayload.sections : {}
    };
    const submittedDocument = {
      sections: submitPayload && submitPayload.sections ? submitPayload.sections : {}
    };
    const editItems = buildDocumentProjectDetailsChanges(
      baselineFormValues,
      values || {},
      initialDocument,
      submittedDocument
    );
    editItems.push(...buildJobStepsChanges(
      { sections: baselineFormValues.sections || {} },
      { sections: (values && values.sections) || {} }
    ));
    editItems.push(...buildEmergencyContactsChanges(
      { sections: baselineFormValues.sections || {} },
      { sections: (values && values.sections) || {} }
    ));
    editItems.push(...buildMethodItemSectionsChanges(
      { sections: baselineFormValues.sections || {} },
      { sections: (values && values.sections) || {} }
    ));
    editItems.push(...buildSwmsContentManagementChanges(
      initialDocument,
      submittedDocument
    ));
    const formData = {
      title: resolveTitle(values),
      document: {
        meta: submitPayload && submitPayload.meta ? submitPayload.meta : (swmsData && swmsData.meta ? swmsData.meta : {}),
        sections: submitPayload && submitPayload.sections ? submitPayload.sections : {}
      },
      version: currentVersion,
      edit_context: createSwmsEditContext({
        initialValues: baselineFormValues,
        submittedValues: values || {},
        internalRevisionId,
        documentName: resolveTitle(values),
        documentId: swmsId,
        items: editItems
      })
    };

    setSubmitting(true);
    if (onSavingChange) onSavingChange(true);

    dispatch(saveSwmsForm(jobId, swmsId, formData))
      .then((message) => {
        notification.success({
          message: Strings.success_title,
          description: message,
          className: "ant-success"
        });
        return refreshForm
          ? refreshForm({ closeOnError: false, showBlockingLoader: false })
          : null;
      })
      .then(() => {
        setSubmitting(false);
        if (onSavingChange) onSavingChange(false);
      })
      .catch((err) => {
        console.log(err);
        setSubmitting(false);
        if (onSavingChange) onSavingChange(false);
        notification.error({
          message: Strings.error_title,
          description: err || Strings.generic_error,
          className: "ant-error"
        });
      });
  };

  return (
    <div className="container-fluid mb-5">
      <div className="row">
        <div className="col-12">
          <form className="mt-3" onSubmit={handleSubmit(onSubmit)}>
            <JobSwmsFormV3SectionRegistry
              swmsData={swmsData}
              loading={loading}
              change={change}
              onResendReviewer={setResendReviewer}
              onCancelApprover={setCancelApprover}
              approvalSubmissionHistory={approvalSubmissionHistory}
              swmsStatus={swmsStatus}
            />

          </form>

          {resendReviewer && (
            <SwmsResendConfirmModal
              reviewer={resendReviewer}
              jobId={jobId}
              swmsId={swmsId}
              swmsName={swmsTitle}
              swmsNumber={swmsNumber}
              onClose={() => setResendReviewer(null)}
              onSuccess={() => { }}
            />
          )}

          {cancelApprover && (
            <SwmsCancelApproverConfirmModal
              approver={cancelApprover}
              jobId={jobId}
              swmsId={swmsId}
              swmsName={swmsTitle}
              swmsNumber={swmsNumber}
              onClose={() => setCancelApprover(null)}
              onSuccess={refreshForm}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default reduxForm({
  form: "job_swms_form_v3",
  enableReinitialize: true,
  destroyOnUnmount: true
})(JobSwmsFormV3Shell);
